import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getCompareRange,
  getPeriodRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

export type ItemMetric = 'units' | 'gross_sales' | 'orders';

interface Args {
  outletId: string | 'all';
  period: PeriodKey;
  by: ItemMetric;
  limit?: number;
}

export interface ItemRow {
  itemId: string;
  itemName: string;
  categoryName: string | null;
  metric: Metric;
  /** Snapshot of the secondary measure (e.g. sales when by='units'). */
  units: number;
  sales: number;
  orders: number;
}

interface Result {
  rows: ItemRow[];
  current: DateRange;
  previous: DateRange;
}

async function fetchRange(outletId: string | 'all', range: DateRange) {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));
  let q = marketing
    .from('mv_item_daily')
    .select('item_id, item_name, category_name, units, gross_sales, orders, date')
    .gte('date', start)
    .lte('date', end);
  if (outletId !== 'all') q = q.eq('outlet_id', outletId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as Array<{
    item_id: string;
    item_name: string;
    category_name: string | null;
    units: number;
    gross_sales: number;
    orders: number;
  }>;
}

function rollup(rows: Awaited<ReturnType<typeof fetchRange>>) {
  const byItem = new Map<
    string,
    { itemId: string; itemName: string; categoryName: string | null; units: number; sales: number; orders: number }
  >();
  for (const r of rows) {
    const entry = byItem.get(r.item_id) ?? {
      itemId: r.item_id,
      itemName: r.item_name,
      categoryName: r.category_name,
      units: 0,
      sales: 0,
      orders: 0,
    };
    entry.units += Number(r.units ?? 0);
    entry.sales += Number(r.gross_sales ?? 0);
    entry.orders += Number(r.orders ?? 0);
    byItem.set(r.item_id, entry);
  }
  return byItem;
}

export function useTopItems({ outletId, period, by, limit = 10 }: Args) {
  const current = getPeriodRange(period === 'custom' ? '7d' : period);
  const previous = getCompareRange(current, 'previous');

  return useQuery<Result>({
    queryKey: ['top-items', outletId, by, current.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([
        fetchRange(outletId, current),
        fetchRange(outletId, previous),
      ]);

      const cur = rollup(curRows);
      const prev = rollup(prevRows);

      const pick = (entry: { units: number; sales: number; orders: number }): number =>
        by === 'units' ? entry.units : by === 'gross_sales' ? entry.sales : entry.orders;

      const rows: ItemRow[] = Array.from(cur.values())
        .map((c) => {
          const p = prev.get(c.itemId);
          return {
            itemId: c.itemId,
            itemName: c.itemName,
            categoryName: c.categoryName,
            units: c.units,
            sales: c.sales,
            orders: c.orders,
            metric: computeMetric(pick(c), p ? pick(p) : 0),
          };
        })
        .sort((a, b) => b.metric.current - a.metric.current)
        .slice(0, limit);

      return { rows, current, previous };
    },
    staleTime: 60_000,
  });
}
